// Single source of truth for conditional logic operators.
// Mirrored in api/app/Services/RuleEvaluator.php — keep operator semantics in sync.
//
// Operator sets per field type mirror JotForm's Show/Hide conditions (the product
// reference). To add a new operator:
//   1) describe it in OPERATORS (label key + whether it needs a value)
//   2) list it under each field type in SOURCE_OPERATORS that should support it
//   3) implement its semantics in RuleEvaluator.evaluateCondition (JS + PHP)

// Operator catalogue. `takesValue` controls whether the UI shows a value picker
// or input next to the operator dropdown.
export const OPERATORS = {
    // Equality — fixed-options and free-text fields.
    equal:           { takesValue: true,  labelKey: 'form_builder.rules.op.equal' },
    not_equal:       { takesValue: true,  labelKey: 'form_builder.rules.op.not_equal' },

    // Substring — free-text fields.
    contains:        { takesValue: true,  labelKey: 'form_builder.rules.op.contains' },
    not_contains:    { takesValue: true,  labelKey: 'form_builder.rules.op.not_contains' },
    starts_with:     { takesValue: true,  labelKey: 'form_builder.rules.op.starts_with' },
    not_starts_with: { takesValue: true,  labelKey: 'form_builder.rules.op.not_starts_with' },
    ends_with:       { takesValue: true,  labelKey: 'form_builder.rules.op.ends_with' },
    not_ends_with:   { takesValue: true,  labelKey: 'form_builder.rules.op.not_ends_with' },

    // Numeric comparison — number / rating / scale.
    less_than:       { takesValue: true,  labelKey: 'form_builder.rules.op.less_than' },
    greater_than:    { takesValue: true,  labelKey: 'form_builder.rules.op.greater_than' },

    // Presence — every field type.
    empty:           { takesValue: false, labelKey: 'form_builder.rules.op.empty' },
    not_empty:       { takesValue: false, labelKey: 'form_builder.rules.op.not_empty' },

    // Date comparison — date fields. *_date compares the full date,
    // *_day compares the day-of-month only (mirrors JotForm).
    before:          { takesValue: true,  labelKey: 'form_builder.rules.op.before' },
    after:           { takesValue: true,  labelKey: 'form_builder.rules.op.after' },
    equal_date:      { takesValue: true,  labelKey: 'form_builder.rules.op.equal_date' },
    not_equal_date:  { takesValue: true,  labelKey: 'form_builder.rules.op.not_equal_date' },
    equal_day:       { takesValue: true,  labelKey: 'form_builder.rules.op.equal_day' },
    not_equal_day:   { takesValue: true,  labelKey: 'form_builder.rules.op.not_equal_day' },
};

// Universal presence operators available for any submittable field.
const PRESENCE = ['empty', 'not_empty'];

// Operator groups by JotForm field semantics.
const CHOICE = ['equal', 'not_equal', ...PRESENCE];
const TEXT = [
    'equal', 'not_equal',
    'contains', 'not_contains',
    'starts_with', 'not_starts_with',
    'ends_with', 'not_ends_with',
    ...PRESENCE,
];
const NUMERIC = ['equal', 'not_equal', ...PRESENCE, 'less_than', 'greater_than'];
const DATE = [
    ...PRESENCE,
    'before', 'after',
    'equal_date', 'not_equal_date',
    'equal_day', 'not_equal_day',
];

// Operators a field type can drive a rule with.
// Anything not listed here cannot be a rule source (e.g. 'description').
export const SOURCE_OPERATORS = {
    // Fixed-options fields.
    dropdown:       [...CHOICE],
    radio:          [...CHOICE],
    radio_button:   [...CHOICE],
    radio_image:    [...CHOICE],
    // Single checkbox submits a boolean — only presence makes sense:
    // "is filled" == checked, "is empty" == unchecked (HF-1222 review).
    checkbox:       [...PRESENCE],
    checkbox_group: [...CHOICE],

    // Free-text and value inputs.
    text:           [...TEXT],
    email:          [...TEXT],
    url:            [...TEXT],
    textarea:       [...TEXT],
    tel:            [...PRESENCE], // phone is masked — only presence, mirrors JotForm

    // Numeric / rating.
    number:         [...NUMERIC],
    numberic_scale: [...NUMERIC],
    slider:         [...NUMERIC],
    star:           [...NUMERIC],

    // Date.
    date:           [...DATE],

    // Presence-only fields (no meaningful value comparison).
    signature:      [...PRESENCE],
    file:           [...PRESENCE],
    multiple_file:  [...PRESENCE],
    booking_widget: [...PRESENCE],
    newsletter:     [...PRESENCE],
};

// Convenience accessors used by the builder UI and RuleEvaluator.

export function operatorsFor(fieldType) {
    return SOURCE_OPERATORS[fieldType] || [];
}

export const SOURCE_TYPES = Object.keys(SOURCE_OPERATORS);

export function isEligibleSource(field) {
    return !!field && SOURCE_TYPES.includes(field.type);
}
